import { MenuIcon } from "@xola/icons";
import { Badge, Modal, Spinner, SubmitButton } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { CatalogItemObject, PickupCatalogItem } from "types";
import { SearchInput } from "@/components/Common/SearchInput";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

interface AddPickupLocationsModalProps {
    selectedItems: PickupOption[];
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (selectedItems: PickupOption[]) => void;
}

export interface PickupOption {
    pickupLocation: { id: string };
    sequence: number;
}

export const AddPickupLocationsModal = ({ selectedItems, isOpen, onClose, onSubmit }: AddPickupLocationsModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <Modal.Header data-testid="add-pickup-locations-modal-header">Add Pickup Locations</Modal.Header>
            <ModalContent selectedItems={selectedItems} onSubmit={onSubmit} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ selectedItems, onSubmit, onClose }: Omit<AddPickupLocationsModalProps, "isOpen">) => {
    const [search, setSearch] = useState("");
    const [selectedSearch, setSelectedSearch] = useState("");
    const [orderedItems, setOrderedItems] = useState<PickupOption[]>(selectedItems ?? []);
    const [localAvailable, setLocalAvailable] = useState<PickupCatalogItem[]>([]);
    const { data: pickupLocations, isLoading } = useAutoFetchCollection<PickupCatalogItem>(
        "catalogItems",
        { object: CatalogItemObject.PICKUP_CATALOG_ITEM },
        {},
    );

    const { data: pickupLocationsProductList, isLoading: isLoadingProductLocations } =
        useAutoFetchCollection<PickupCatalogItem>(
            "catalogItems",
            { object: CatalogItemObject.PICKUP_PRODUCT_CATALOG_ITEM },
            {},
        );

    useEffect(() => {
        if (pickupLocations && pickupLocationsProductList) {
            const filteredProductPickupLocations = pickupLocationsProductList.filter((productLocation) =>
                selectedItems.some((selectedItem) => selectedItem.pickupLocation.id === productLocation.id),
            );

            const productIds = new Set(filteredProductPickupLocations.map((item) => item?.parent?.id));

            setLocalAvailable(
                [
                    ...pickupLocations.filter((item) => !productIds.has(item.id)),
                    ...filteredProductPickupLocations,
                ].filter((item) => !selectedItems.some((sel) => sel?.pickupLocation?.id === item.id)),
            );
        }
    }, [pickupLocations, pickupLocationsProductList, selectedItems]);
    const findPickupLocation = useCallback(
        (id: string) => [...pickupLocations, ...pickupLocationsProductList]?.find((loc) => loc.id === id),
        [pickupLocations, pickupLocationsProductList],
    );

    const availableItems = useMemo(() => {
        return localAvailable.filter(
            (item) =>
                !orderedItems.some((sel) => sel?.pickupLocation?.id === item.id) &&
                item?.name?.toLowerCase()?.includes(search.toLowerCase()),
        );
    }, [localAvailable, orderedItems, search]);

    const selectedFilteredItems = useMemo(() => {
        return orderedItems.filter((sel) => {
            const pickup = findPickupLocation(sel?.pickupLocation?.id);
            return pickup?.name?.toLowerCase()?.includes(selectedSearch.toLowerCase());
        });
    }, [orderedItems, selectedSearch, findPickupLocation]);

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        if (source.droppableId === "available" && destination.droppableId === "available") {
            const [moved, updated] = removeFromArray(localAvailable, source.index);
            updated.splice(destination.index, 0, moved);
            setLocalAvailable(updated);
            return;
        }

        if (source.droppableId === "selected" && destination.droppableId === "selected") {
            const [moved, updated] = removeFromArray(orderedItems, source.index);
            updated.splice(destination.index, 0, moved);
            setOrderedItems(updated.map((item, index) => ({ ...item, sequence: index + 1 })));
            return;
        }

        if (source.droppableId === "available" && destination.droppableId === "selected") {
            const [moved, updatedAvailable] = removeFromArray(localAvailable, source.index);
            setLocalAvailable(updatedAvailable);

            let insertIndex = destination.index;
            if (selectedSearch) {
                const realIndices = selectedFilteredItems.map((item) =>
                    orderedItems.findIndex((orderItem) => orderItem.pickupLocation.id === item.pickupLocation.id),
                );
                insertIndex = realIndices[destination.index] ?? orderedItems.length;
            }

            const newOrdered = [
                ...orderedItems.slice(0, insertIndex),
                { pickupLocation: { id: moved.id }, sequence: 0 },
                ...orderedItems.slice(insertIndex),
            ];
            setOrderedItems(newOrdered.map((item, index) => ({ ...item, sequence: index + 1 })));
            return;
        }

        if (source.droppableId === "selected" && destination.droppableId === "available") {
            const [moved, updatedSelected] = removeFromArray(orderedItems, source.index);
            setOrderedItems(updatedSelected.map((item, index) => ({ ...item, sequence: index + 1 })));

            const movedItem = pickupLocations?.find((loc) => loc.id === moved.pickupLocation.id);
            if (movedItem) {
                const updatedAvailable = [...localAvailable];
                updatedAvailable.splice(destination.index, 0, movedItem);
                setLocalAvailable(updatedAvailable);
            }
            return;
        }
    };

    if (isLoading || isLoadingProductLocations) return <Spinner />;

    return (
        <Modal.Body>
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex w-full gap-4">
                    <div className="flex w-1/2 flex-col gap-4">
                        <p className="mt-4 text-md font-bold">All Locations</p>
                        <SearchInput onChange={setSearch} />
                        <Droppable
                            droppableId="available"
                            renderClone={(provided, _, rubric) => {
                                const item = availableItems[rubric.source.index];
                                return (
                                    <div
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ref={provided.innerRef}
                                        data-testid="add-pickup-location-available-item"
                                        className="border-gray-200 hover:bg-gray-50 flex cursor-grab items-center whitespace-normal bg-white p-2 px-4"
                                    >
                                        {item?.name}
                                    </div>
                                );
                            }}
                        >
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="border-gray-200 flex  flex-col rounded border border-gray-light"
                                >
                                    <div className="h-[300px] overflow-y-auto">
                                        {availableItems.map((item, index) => (
                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        data-testid="add-pickup-location-available-item"
                                                        className="border-gray-200 hover:bg-gray-50 flex cursor-grab items-center justify-between border-b border-gray-light p-4 last:border-b-0 hover:bg-gray-lighter"
                                                    >
                                                        {item.name}
                                                        <MenuIcon />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </div>

                    <div className="flex w-1/2 flex-col gap-4">
                        <p className="mt-4 text-md font-bold">
                            Selected Locations <Badge>{selectedFilteredItems.length}</Badge>
                        </p>
                        <SearchInput onChange={setSelectedSearch} />
                        <Droppable
                            droppableId="selected"
                            renderClone={(provided, _, rubric) => {
                                const sel = orderedItems[rubric.source.index];
                                const pickup = findPickupLocation(sel.pickupLocation.id);
                                return (
                                    <div
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ref={provided.innerRef}
                                        data-testid="add-pickup-location-selected-item"
                                        className="border-gray-200 hover:bg-gray-50 flex cursor-grab items-center whitespace-normal bg-white p-2 px-4"
                                    >
                                        {pickup?.name}
                                    </div>
                                );
                            }}
                        >
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="border-gray-200 flex flex-col rounded border border-gray-light"
                                >
                                    <div className="h-[300px] overflow-y-auto">
                                        {selectedFilteredItems.map((sel, index) => {
                                            const pickup = findPickupLocation(sel.pickupLocation.id);
                                            return (
                                                <Draggable
                                                    key={sel.pickupLocation.id}
                                                    draggableId={sel.pickupLocation.id}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            data-testid="add-pickup-location-selected-item"
                                                            className="border-gray-200 hover:bg-gray-50 flex items-center justify-between border-b border-gray-light p-4 last:border-b-0 hover:bg-gray-lighter"
                                                        >
                                                            <p className="pl-2">{pickup?.name}</p>
                                                            <MenuIcon />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </div>
                </div>
            </DragDropContext>

            <Modal.Footer>
                <SecondaryButton data-testid="add-pickup-locations-cancel-button" onClick={onClose}>
                    Cancel
                </SecondaryButton>
                <SubmitButton
                    data-testid="add-pickup-locations-save-button"
                    type="button"
                    onClick={() => onSubmit(orderedItems)}
                >
                    Save
                </SubmitButton>
            </Modal.Footer>
        </Modal.Body>
    );
};

const removeFromArray = <T,>(array: T[], index: number): [T, T[]] => {
    const newArray = [...array];
    const [removed] = newArray.splice(index, 1);
    return [removed, newArray];
};
